import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"
import _imports_0 from '@/assets/icons/doctorAvatar.svg'


const _hoisted_1 = {
  class: "flex flex-col gap-y-4 text-left",
  "data-testid": "document-review-split-view"
}
const _hoisted_2 = {
  key: 0,
  class: "grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 min-h-[560px]",
  "data-testid": "document-review-skeleton"
}
const _hoisted_3 = { class: "flex flex-col gap-2 rounded-lg border border-[#d8d8e2] p-3 animate-pulse" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("div", { class: "h-3.5 w-40 bg-slate-200 rounded" }, null, -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("div", { class: "h-3 w-56 bg-slate-200 rounded" }, null, -1)
const _hoisted_6 = [
  _hoisted_4,
  _hoisted_5
]
const _hoisted_7 = /*#__PURE__*/_createElementVNode("div", { class: "rounded-lg border border-[#d8d8e2] bg-[#f0f0f4] animate-pulse" }, null, -1)
const _hoisted_8 = {
  key: 3,
  class: "grid grid-cols-1 lg:grid-cols-[340px_minmax(0,1fr)] gap-4 items-start"
}
const _hoisted_9 = { class: "flex flex-col rounded-lg border border-[#d8d8e2] bg-white overflow-hidden lg:sticky lg:top-2" }
const _hoisted_10 = { class: "flex items-center justify-between px-3 py-2 bg-[#f0f0f4] text-[12px]" }
const _hoisted_11 = { class: "font-bold text-textPrimary" }
const _hoisted_12 = { class: "flex items-center gap-1 cursor-pointer text-[#52526f]" }
const _hoisted_13 = { class: "flex flex-col gap-1 px-3 pt-2.5 pb-1.5" }
const _hoisted_14 = { class: "flex items-center justify-between gap-2" }
const _hoisted_15 = { class: "text-[13px] font-bold text-textPrimary truncate" }
const _hoisted_16 = ["data-testid", "onClick"]
const _hoisted_17 = ["disabled", "checked", "aria-label", "data-testid", "onChange"]
const _hoisted_18 = { class: "flex-1 text-[12px] text-textPrimary truncate" }
const _hoisted_19 = {
  key: 0,
  class: "p-2 border-t border-[#e8e8ed]"
}
const _hoisted_20 = {
  key: 1,
  class: "text-[11px] text-[#7f7f8e] px-1"
}
const _hoisted_21 = {
  key: 0,
  class: "flex flex-col gap-4 rounded-lg border border-[#d8d8e2] bg-white p-4",
  "data-testid": "document-review-active"
}
const _hoisted_22 = { class: "flex flex-wrap items-start justify-between gap-3" }
const _hoisted_23 = { class: "flex items-start gap-3 min-w-0" }
const _hoisted_24 = /*#__PURE__*/_createElementVNode("img", {
  src: _imports_0,
  alt: "Avatar do médico",
  class: "w-full h-full rounded-full object-cover",
  "data-testid": "document-review-avatar-fallback"
}, null, -1)
const _hoisted_25 = { class: "min-w-0" }
const _hoisted_26 = { class: "text-base font-bold text-textPrimary" }
const _hoisted_27 = { class: "text-xs text-[#52526f]" }
const _hoisted_28 = {
  key: 0,
  class: "text-xs font-bold text-alert"
}
const _hoisted_29 = { class: "flex items-center gap-2 shrink-0" }
const _hoisted_30 = { class: "text-xs text-[#7f7f8e]" }

import { computed, nextTick, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiFileDocumentCheckOutline,
  mdiFileDocumentRemoveOutline
} from '@mdi/js'

import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'
import EmptyState from '@/components/designSystem/EmptyState/EmptyState.vue'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import GhostButton from '@/components/designSystem/Buttons/GhostButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ConfirmDeleteAction from '@/components/designSystem/modals/confirm.delete.action.vue'
import QueueBulkBar from '@/components/designSystem/QueueViews/QueueBulkBar.vue'
import type { QueueBulkAction } from '@/components/designSystem/QueueViews/types'
import DocumentStatusBadge from '@/components/ProfessionalDocuments/DocumentStatusBadge.vue'
import { useErrorToast } from '@/composables/useErrorToast'
import { resolveAvatarSrc } from '@/utils/avatar'
import { EMPTY_REVIEW_FILTERS, useDocumentReviewStore } from '@/pinia-store/useDocumentReviewStore'
import type { ApproveTarget, DecisionTarget } from '@/pinia-store/useDocumentReviewStore'
import {
  CATALOG_LOAD_ERROR,
  useProfessionalDocumentCatalogStore
} from '@/pinia-store/useProfessionalDocumentCatalogStore'
import { ProfessionalDocumentStatus } from '@/types/professional-documents.types'
import type {
  BulkDecisionResponse,
  RejectVersionPayload
} from '@/types/professional-documents.types'

import {
  APPROVE_ERROR,
  DETAIL_LOAD_ERROR,
  QUEUE_LOAD_ERROR,
  REJECT_ERROR,
  UNDO_ERROR
} from '../constants'
import {
  decisionSummary,
  firstInReviewId,
  flattenQueue,
  formatStationStart,
  isInReview,
  nextInReviewId
} from '../model'
import type { DecisionVerb, ReviewQueueEntry } from '../model'
import { useDocumentReviewAccess } from '../composables/useDocumentReviewAccess'
import { useDocumentReviewShortcuts } from '../composables/useDocumentReviewShortcuts'
import ApproveDocumentModal from './ApproveDocumentModal.vue'
import type { ApproveModalItem } from './ApproveDocumentModal.vue'
import DocumentQueueFilterBar from './DocumentQueueFilterBar.vue'
import DocumentReviewPanel from './DocumentReviewPanel.vue'
import ProfessionalTags from './ProfessionalTags.vue'
import RejectDocumentModal from './RejectDocumentModal.vue'
import type { RejectModalItem } from './RejectDocumentModal.vue'
import WaitingSince from './WaitingSince.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'DocumentReviewSplitView',
  setup(__props) {

const BULK_ACTIONS: QueueBulkAction[] = [{ key: 'reject', label: 'Reprovar' }]

const store = useDocumentReviewStore()
const catalog = useProfessionalDocumentCatalogStore()
const {
  items,
  filters,
  isLoading,
  isUnavailable,
  loadError,
  hasActiveFilters,
  details,
  loadingDetailFor,
  isSaving
} = storeToRefs(store)
const toast = useToast()
const { notifyError } = useErrorToast()

const onlyInReview = ref(false)
const activeId = ref<number | null>(null)
const { canReview } = useDocumentReviewAccess()
const selectedIds = ref<number[]>([])
const listRef = ref<HTMLElement | null>(null)

const sequence = computed(() => flattenQueue(items.value, onlyInReview.value))
const groups = computed(() =>
  items.value
    .map((professional) => ({
      professional,
      entries: sequence.value.filter((entry) => entry.professional.userId === professional.userId)
    }))
    .filter((group) => group.entries.length > 0)
)
const inReviewCount = computed(() => sequence.value.filter(isInReview).length)
const position = computed(() =>
  sequence.value.findIndex((entry) => entry.document.documentId === activeId.value)
)
const active = computed<ReviewQueueEntry | null>(() => sequence.value[position.value] ?? null)
const activeDetail = computed(() =>
  active.value ? (details.value[active.value.professional.userId] ?? null) : null
)
const selectedEntries = computed(() =>
  sequence.value.filter((entry) => selectedIds.value.includes(entry.document.documentId))
)
const isFiltered = computed(() => hasActiveFilters.value || onlyInReview.value)

/** Foto do médico ativo (vem na fila); sem foto, o slot mostra o avatar padrão. */
const activeAvatarSrc = computed(() => resolveAvatarSrc(active.value?.professional.avatarUrl))

const professionalSubtitle = computed(() => {
  const professional = active.value?.professional
  if (!professional) return ''
  const crm = professional.crm ? `CRM ${professional.crm}/${professional.crmUf ?? ''}` : null
  return [crm, ...professional.companies.map((company) => company.name)].filter(Boolean).join(' · ')
})

const loadErrorMessage = computed(() => {
  const kept = 'Seus filtros foram mantidos.'
  if (!loadError.value || loadError.value === QUEUE_LOAD_ERROR) return kept
  return `${loadError.value.replace(/\.?$/, '.')} ${kept}`
})

const detailDocument = (entry: ReviewQueueEntry) =>
  details.value[entry.professional.userId]?.documents.find(
    (item) => item.documentId === entry.document.documentId
  ) ?? null

/**
 * Só Em Análise com arquivo. O detalhe, quando já carregou, manda (R19): é mais novo que a fila
 * e marca o documento restrito (D80, `currentVersion: null`), que não se aprova nem por atalho.
 */
function canDecide(entry: ReviewQueueEntry): boolean {
  if (!isInReview(entry) || entry.document.currentVersionId === null) return false
  const item = detailDocument(entry)
  return (
    !item || (item.status === ProfessionalDocumentStatus.IN_REVIEW && item.currentVersion !== null)
  )
}

/** Seleção só do que segue visível e decidível (filtro, refetch, decisão de outro revisor). */
const keepDecidable = (ids: number[], seq: ReviewQueueEntry[]) =>
  ids.filter((id) => seq.some((entry) => entry.document.documentId === id && canDecide(entry)))

watch(
  sequence,
  (seq) => {
    const kept = keepDecidable(selectedIds.value, seq)
    if (kept.length !== selectedIds.value.length) selectedIds.value = kept
    if (seq.some((entry) => entry.document.documentId === activeId.value)) return
    activeId.value = firstInReviewId(seq)
  },
  { immediate: true }
)

watch(
  () => active.value?.professional.userId,
  (userId) => {
    if (userId === undefined) return
    store.fetchProfessional(userId).catch((error) => notifyError(error, DETAIL_LOAD_ERROR))
  },
  { immediate: true }
)

// Com J/K o item ativo pode sair da área visível da lista (rolagem própria de 640px).
watch(activeId, async (id) => {
  await nextTick()
  const item = listRef.value?.querySelector(`[data-testid="document-queue-item-${id}"]`)
  // `?.()`: o jsdom dos testes não implementa `scrollIntoView`.
  item?.scrollIntoView?.({ block: 'nearest' })
})

function move(delta: number) {
  const target = sequence.value[position.value + delta]
  if (target) activeId.value = target.document.documentId
}

function toggleSelection(documentId: number) {
  selectedIds.value = selectedIds.value.includes(documentId)
    ? selectedIds.value.filter((id) => id !== documentId)
    : [...selectedIds.value, documentId]
}

function clearFilters() {
  onlyInReview.value = false
  if (hasActiveFilters.value) void store.setFilters({ ...EMPTY_REVIEW_FILTERS })
}

// --- decisões --------------------------------------------------------------------
const actionEntries = ref<ReviewQueueEntry[]>([])
const approveOpen = ref(false)
const rejectOpen = ref(false)
const undoTarget = ref<(DecisionTarget & { label: string }) | null>(null)

const approveItems = computed<ApproveModalItem[]>(() =>
  actionEntries.value.map((entry) => ({
    versionId: entry.document.currentVersionId as number,
    userId: entry.professional.userId,
    documentName: entry.document.name,
    professionalName: entry.professional.name,
    crmLabel: entry.professional.crm
      ? `CRM ${entry.professional.crm}/${entry.professional.crmUf ?? ''}`
      : null,
    hasExpiry: entry.document.hasExpiry,
    acceptsAuthenticityCode: entry.document.acceptsAuthenticityCode,
    description: detailDocument(entry)?.description ?? null
  }))
)
const rejectItems = computed<RejectModalItem[]>(() =>
  actionEntries.value.map((entry) => ({
    versionId: entry.document.currentVersionId as number,
    userId: entry.professional.userId,
    documentName: entry.document.name,
    professionalName: entry.professional.name
  }))
)
const undoDescription = computed(() =>
  undoTarget.value
    ? `${undoTarget.value.label} volta para Em Análise. A decisão desfeita fica no histórico.`
    : ''
)

function openApprove(entries: ReviewQueueEntry[]) {
  actionEntries.value = entries.filter(canDecide)
  if (actionEntries.value.length) approveOpen.value = true
}
function openReject(entries: ReviewQueueEntry[]) {
  actionEntries.value = entries.filter(canDecide)
  if (actionEntries.value.length) rejectOpen.value = true
}

/**
 * A store já recarregou fila e detalhes. O próximo sai da ordem que o revisor via (`before`, D23),
 * pulando o que acabou de ser decidido e o que deixou de estar Em Análise (outro revisor).
 * Na seleção ficam só os que falharam e seguem decidíveis (R10).
 */
function afterDecision(
  result: BulkDecisionResponse,
  verb: DecisionVerb,
  before: ReviewQueueEntry[],
  from: number
) {
  const decided = actionEntries.value
    .filter((entry) => result.succeeded.includes(entry.document.currentVersionId as number))
    .map((entry) => entry.document.documentId)
  const stillOpen = new Set(
    sequence.value.filter(canDecide).map((entry) => entry.document.documentId)
  )
  const closed = before.map((entry) => entry.document.documentId).filter((id) => !stillOpen.has(id))
  const next = nextInReviewId(before, from, [...decided, ...closed])
  if (next !== null) activeId.value = next
  selectedIds.value = keepDecidable(
    selectedIds.value.filter((id) => !decided.includes(id)),
    sequence.value
  )
  const summary = decisionSummary(result, verb)
  toast[summary.tone](summary.message)
}

/** Erro (ex.: 409 de decisão concorrente): mostra a mensagem e fica no mesmo documento. */
async function decide(
  verb: DecisionVerb,
  run: () => Promise<BulkDecisionResponse>,
  fallback: string
) {
  const before = sequence.value
  const from = position.value
  let result: BulkDecisionResponse
  try {
    result = await run()
  } catch (error) {
    notifyError(error, fallback)
    return
  } finally {
    approveOpen.value = false
    rejectOpen.value = false
  }
  afterDecision(result, verb, before, from)
}

function confirmApprove(targets: ApproveTarget[]) {
  return decide('aprovado', () => store.approve(targets), APPROVE_ERROR)
}

function confirmReject(targets: DecisionTarget[], payload: RejectVersionPayload) {
  return decide('reprovado', () => store.reject(targets, payload), REJECT_ERROR)
}

function requestUndo(versionId: number) {
  const entry = active.value
  if (!entry) return
  undoTarget.value = {
    versionId,
    userId: entry.professional.userId,
    label: `${entry.document.name} de ${entry.professional.name}`
  }
}

async function confirmUndo() {
  const target = undoTarget.value
  if (!target) return
  undoTarget.value = null
  try {
    await store.undo(target)
    toast.info('Decisão desfeita — documento voltou para Em Análise')
  } catch (error) {
    notifyError(error, UNDO_ERROR)
  }
}

useDocumentReviewShortcuts({
  isEnabled: () =>
    !isLoading.value &&
    !isSaving.value &&
    !approveOpen.value &&
    !rejectOpen.value &&
    undoTarget.value === null &&
    active.value !== null,
  onNext: () => move(1),
  onPrevious: () => move(-1),
  onApprove: () => canReview.value && active.value && openApprove([active.value]),
  onReject: () => canReview.value && active.value && openReject([active.value])
})

// Na criação (não no `onMounted`): o primeiro render já sai com o skeleton, sem piscar "Fila zerada".
void store.fetchQueue()
catalog.ensureLoaded().catch((error) => notifyError(error, CATALOG_LOAD_ERROR))

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(isUnavailable))
      ? (_openBlock(), _createBlock(EmptyState, {
          key: 0,
          icon: _unref(mdiFileDocumentRemoveOutline),
          title: "Revisão de documentos indisponível",
          description: "A revisão de documentos ainda não foi liberada neste ambiente.",
          variant: "dashed"
        }, null, 8, ["icon"]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createVNode(DocumentQueueFilterBar, {
            filters: _unref(filters),
            onChange: _unref(store).setFilters
          }, null, 8, ["filters", "onChange"]),
          (_unref(isLoading))
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(7, (n) => {
                    return _createElementVNode("div", {
                      key: n,
                      class: "flex flex-col gap-1.5 py-2"
                    }, _hoisted_6)
                  }), 64))
                ]),
                _hoisted_7
              ]))
            : (_unref(loadError))
              ? (_openBlock(), _createBlock(ReportErrorState, {
                  key: 1,
                  title: _unref(QUEUE_LOAD_ERROR),
                  message: loadErrorMessage.value,
                  onRetry: _cache[0] || (_cache[0] = ($event: any) => (_unref(store).fetchQueue()))
                }, null, 8, ["title", "message"]))
              : (!groups.value.length)
                ? (_openBlock(), _createBlock(EmptyState, {
                    key: 2,
                    icon: _unref(mdiFileDocumentCheckOutline),
                    title: 
          isFiltered.value
            ? 'Nenhum documento com esses filtros'
            : 'Fila zerada — nada aguardando revisão'
        ,
                    description: 
          isFiltered.value
            ? 'Ajuste ou limpe os filtros para ver mais documentos.'
            : 'Quando um médico enviar documentos pelo Drive, eles aparecem aqui como Em Análise.'
        ,
                    variant: "dashed"
                  }, {
                    default: _withCtx(() => [
                      (isFiltered.value)
                        ? (_openBlock(), _createBlock(GhostButton, {
                            key: 0,
                            text: "Limpar filtros",
                            width: "180px",
                            "data-testid": "document-review-clear-filters",
                            onClick: clearFilters
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }, 8, ["icon", "title", "description"]))
                : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    _createElementVNode("aside", _hoisted_9, [
                      _createElementVNode("div", _hoisted_10, [
                        _createElementVNode("span", _hoisted_11, _toDisplayString(inReviewCount.value) + " em análise · " + _toDisplayString(groups.value.length) + " " + _toDisplayString(groups.value.length === 1 ? 'médico' : 'médicos'), 1),
                        _createElementVNode("label", _hoisted_12, [
                          _withDirectives(_createElementVNode("input", {
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((onlyInReview).value = $event)),
                            type: "checkbox",
                            class: "accent-[#2f7f91]",
                            "data-testid": "document-queue-only-in-review"
                          }, null, 512), [
                            [_vModelCheckbox, onlyInReview.value]
                          ]),
                          _createTextVNode(" só Em Análise ")
                        ])
                      ]),
                      _createElementVNode("div", {
                        ref_key: "listRef",
                        ref: listRef,
                        class: "max-h-[640px] overflow-y-auto",
                        "data-testid": "document-queue-list"
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value, (group) => {
                          return (_openBlock(), _createElementBlock("section", {
                            key: group.professional.userId,
                            class: "border-t border-[#e8e8ed] first:border-t-0"
                          }, [
                            _createElementVNode("div", _hoisted_13, [
                              _createElementVNode("div", _hoisted_14, [
                                _createElementVNode("span", _hoisted_15, _toDisplayString(group.professional.name), 1),
                                _createVNode(WaitingSince, {
                                  since: group.professional.waitingSince
                                }, null, 8, ["since"])
                              ]),
                              _createVNode(ProfessionalTags, {
                                tags: group.professional.tags
                              }, null, 8, ["tags"])
                            ]),
                            _createElementVNode("ul", null, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.entries, (entry) => {
                                return (_openBlock(), _createElementBlock("li", {
                                  key: entry.document.documentId,
                                  class: _normalizeClass(["flex items-center gap-2 pl-3 pr-2 py-1.5 cursor-pointer border-l-[3px]", 
                    entry.document.documentId === activeId.value
                      ? 'bg-primary5 border-l-primary30'
                      : 'border-l-transparent hover:bg-[#fafafa]'
                  ]),
                                  "data-testid": `document-queue-item-${entry.document.documentId}`,
                                  onClick: ($event: any) => (activeId.value = entry.document.documentId)
                                }, [
                                  (_unref(canReview))
                                    ? (_openBlock(), _createElementBlock("input", {
                                        key: 0,
                                        type: "checkbox",
                                        class: "accent-[#2f7f91]",
                                        disabled: !canDecide(entry),
                                        checked: selectedIds.value.includes(entry.document.documentId),
                                        "aria-label": `Selecionar ${entry.document.name} de ${entry.professional.name}`,
                                        "data-testid": `document-queue-select-${entry.document.documentId}`,
                                        onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"])),
                                        onChange: ($event: any) => (toggleSelection(entry.document.documentId))
                                      }, null, 40, _hoisted_17))
                                    : _createCommentVNode("", true),
                                  _createElementVNode("span", _hoisted_18, _toDisplayString(entry.document.name), 1),
                                  _createVNode(DocumentStatusBadge, {
                                    status: entry.document.status,
                                    "is-legacy": entry.document.isLegacy
                                  }, null, 8, ["status", "is-legacy"])
                                ], 10, _hoisted_16))
                              }), 128))
                            ])
                          ]))
                        }), 128))
                      ], 512),
                      (_unref(canReview))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                            (selectedIds.value.length)
                              ? (_openBlock(), _createBlock(QueueBulkBar, {
                                  key: 0,
                                  "selected-count": selectedIds.value.length,
                                  "pending-count": inReviewCount.value,
                                  "pending-label": "em análise",
                                  "primary-label": `Aprovar ${selectedIds.value.length}`,
                                  actions: BULK_ACTIONS,
                                  "test-id": "document-queue-bulk",
                                  onPrimary: _cache[3] || (_cache[3] = ($event: any) => (openApprove(selectedEntries.value))),
                                  onAction: _cache[4] || (_cache[4] = ($event: any) => (openReject(selectedEntries.value))),
                                  onClear: _cache[5] || (_cache[5] = ($event: any) => (selectedIds.value = []))
                                }, null, 8, ["selected-count", "pending-count", "primary-label"]))
                              : (_openBlock(), _createElementBlock("p", _hoisted_20, " Marque documentos Em Análise para aprovar ou reprovar em lote. "))
                          ]))
                        : _createCommentVNode("", true)
                    ]),
                    (active.value)
                      ? (_openBlock(), _createElementBlock("section", _hoisted_21, [
                          _createElementVNode("header", _hoisted_22, [
                            _createElementVNode("div", _hoisted_23, [
                              _createVNode(ProtectedAvatar, {
                                src: activeAvatarSrc.value,
                                size: 40,
                                "data-testid": "document-review-avatar"
                              }, {
                                default: _withCtx(() => [
                                  _hoisted_24
                                ]),
                                _: 1
                              }, 8, ["src"]),
                              _createElementVNode("div", _hoisted_25, [
                                _createElementVNode("p", _hoisted_26, _toDisplayString(active.value.professional.name), 1),
                                _createElementVNode("p", _hoisted_27, _toDisplayString(professionalSubtitle.value), 1),
                                (activeDetail.value?.nextStation)
                                  ? (_openBlock(), _createElementBlock("p", _hoisted_28, " Próximo plantão " + _toDisplayString(_unref(formatStationStart)(activeDetail.value.nextStation.start)) + " — " + _toDisplayString(activeDetail.value.nextStation.scheduleName), 1))
                                  : _createCommentVNode("", true)
                              ])
                            ]),
                            _createElementVNode("div", _hoisted_29, [
                              _createElementVNode("span", _hoisted_30, _toDisplayString(position.value + 1) + " de " + _toDisplayString(sequence.value.length), 1),
                              _createVNode(OutlineButton, {
                                text: "Anterior",
                                width: "auto",
                                height: "32px",
                                "prepend-icon": _unref(mdiChevronLeft),
                                disabled: position.value <= 0,
                                "data-testid": "document-review-previous",
                                onClick: _cache[6] || (_cache[6] = ($event: any) => (move(-1)))
                              }, null, 8, ["prepend-icon", "disabled"]),
                              _createVNode(FilledButton, {
                                text: "Próximo",
                                width: "auto",
                                height: "32px",
                                "append-icon": _unref(mdiChevronRight),
                                disabled: position.value >= sequence.value.length - 1,
                                "data-testid": "document-review-next",
                                onClick: _cache[7] || (_cache[7] = ($event: any) => (move(1)))
                              }, null, 8, ["append-icon", "disabled"])
                            ])
                          ]),
                          _createVNode(DocumentReviewPanel, {
                            entry: active.value,
                            detail: activeDetail.value,
                            "is-loading-detail": _unref(loadingDetailFor) === active.value.professional.userId,
                            "show-shortcuts": _unref(canReview),
                            "can-review": _unref(canReview),
                            onApprove: _cache[8] || (_cache[8] = ($event: any) => (openApprove([active.value]))),
                            onReject: _cache[9] || (_cache[9] = ($event: any) => (openReject([active.value]))),
                            onUndo: requestUndo
                          }, null, 8, ["entry", "detail", "is-loading-detail", "show-shortcuts", "can-review"])
                        ]))
                      : _createCommentVNode("", true)
                  ]))
        ], 64)),
    _createVNode(ApproveDocumentModal, {
      modelValue: approveOpen.value,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((approveOpen).value = $event)),
      items: approveItems.value,
      "is-saving": _unref(isSaving),
      onConfirm: confirmApprove
    }, null, 8, ["modelValue", "items", "is-saving"]),
    _createVNode(RejectDocumentModal, {
      modelValue: rejectOpen.value,
      "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((rejectOpen).value = $event)),
      items: rejectItems.value,
      "is-saving": _unref(isSaving),
      onConfirm: confirmReject
    }, null, 8, ["modelValue", "items", "is-saving"]),
    _createVNode(ConfirmDeleteAction, {
      show: undoTarget.value !== null,
      title: "Desfazer decisão?",
      description: undoDescription.value,
      "text-button": "Desfazer decisão",
      onCloseModal: _cache[12] || (_cache[12] = ($event: any) => (undoTarget.value = null)),
      onHandleConfirm: confirmUndo
    }, null, 8, ["show", "description"])
  ]))
}
}

})